import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserX, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Post-import panel: lists every lead whose "assigned to" value in the sheet
 * couldn't be resolved to a workspace user. SuperAdmin can pick a member per row
 * (or in bulk for identical raw values) and assign them in one click.
 */
export default function UnmatchedAssignees({ rows, members, onDone }) {
  const [choices, setChoices] = useState({}); // { [lead_id]: user_id }
  const [busy, setBusy] = useState(false);

  // Group by the raw assignee string so "Ashish Mehrotra" can be bulk-resolved.
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const key = r.assignee_raw || "—";
      if (!g[key]) g[key] = [];
      g[key].push(r);
    });
    return g;
  }, [rows]);

  const setGroup = (raw, user_id) => {
    setChoices((prev) => {
      const next = { ...prev };
      (groups[raw] || []).forEach((r) => { next[r.lead_id] = user_id; });
      return next;
    });
  };

  const assignAll = async () => {
    const picked = Object.entries(choices).filter(([, uid]) => uid);
    if (!picked.length) {
      toast.error("Pick at least one member to assign.");
      return;
    }
    setBusy(true);
    try {
      // group by assignee to minimise API calls
      const byUser = {};
      picked.forEach(([lid, uid]) => {
        if (!byUser[uid]) byUser[uid] = [];
        byUser[uid].push(lid);
      });
      for (const [uid, ids] of Object.entries(byUser)) {
        await api.post("/leads/bulk-update", { ids, assigned_to: uid });
      }
      // Persist each unique raw→user pair as an alias so future imports auto-resolve.
      const rowById = Object.fromEntries(rows.map((r) => [r.lead_id, r]));
      const aliases = {};
      picked.forEach(([lid, uid]) => {
        const raw = rowById[lid]?.assignee_raw;
        if (raw) aliases[raw] = uid;
      });
      const savePromises = Object.entries(aliases).map(([raw, uid]) =>
        api.post("/assignee-aliases", { raw, user_id: uid }).catch(() => null)
      );
      await Promise.all(savePromises);
      const savedCount = Object.keys(aliases).length;
      toast.success(
        savedCount
          ? `Assigned ${picked.length} lead(s). Remembered ${savedCount} alias${savedCount === 1 ? "" : "es"} for next time.`
          : `Assigned ${picked.length} lead(s).`
      );
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const totalPicked = Object.values(choices).filter(Boolean).length;
  const totalUnmatched = rows.length;

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-5" data-testid="unmatched-assignees-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
          <UserX className="w-4 h-4 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            {totalUnmatched} lead{totalUnmatched === 1 ? "" : "s"} couldn't be auto-assigned
          </p>
          <p className="text-xs text-amber-800">
            Match each name below to a team member (or leave blank) — everything is currently owned by you.
          </p>
        </div>
        <Button
          onClick={assignAll}
          disabled={busy || totalPicked === 0}
          className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
          data-testid="assign-all-unmatched-btn"
        >
          <Check className="w-4 h-4 mr-1.5" />
          {busy ? "Assigning…" : `Assign ${totalPicked || ""} lead${totalPicked === 1 ? "" : "s"}`}
        </Button>
      </div>

      <div className="space-y-3">
        {Object.entries(groups).map(([raw, groupRows]) => (
          <div key={raw} className="rounded-lg bg-white border border-amber-200 p-3" data-testid={`unmatched-group-${raw}`}>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-sm font-medium text-slate-900 flex-1 min-w-0 truncate">
                Sheet value: <span className="font-semibold">{raw}</span>
              </span>
              <span className="text-[11px] text-slate-500">{groupRows.length} lead{groupRows.length === 1 ? "" : "s"}</span>
              <Select value="" onValueChange={(v) => setGroup(raw, v)}>
                <SelectTrigger className="w-56 h-8 bg-slate-50 border-slate-200 text-xs" data-testid={`assign-group-select-${raw}`}>
                  <SelectValue placeholder="Assign entire group…" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ul className="space-y-1.5">
              {groupRows.map((r) => (
                <li key={r.lead_id} className="flex items-center gap-2 flex-wrap" data-testid={`unmatched-row-${r.lead_id}`}>
                  <span className="text-xs text-slate-500 w-10 flex-shrink-0">#{r.row}</span>
                  <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{r.lead_name}</span>
                  <Select
                    value={choices[r.lead_id] || ""}
                    onValueChange={(v) => setChoices((prev) => ({ ...prev, [r.lead_id]: v }))}
                  >
                    <SelectTrigger className="w-48 h-8 bg-white border-slate-200 text-xs" data-testid={`assign-row-select-${r.lead_id}`}>
                      <SelectValue placeholder="Pick a member…" />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
